import { formatMinor, type CcaRow, type DataExport, type T5018Row } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Input, Pill, Skeleton } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import {
  dataExportPartUrl,
  dataExportsQuery,
  requestDataExport,
} from '../../shared/api/data-export-queries.js';
import { bookkeepingReferenceQuery } from '../../shared/api/bookkeeping-queries.js';
import {
  ccaQuery,
  closeFiscalYear,
  postCca,
  reopenFiscalYear,
  setCcaInput,
  setFiscalYearEnd,
  t5018Query,
  yearEndKeys,
  yearEndStatusQuery,
} from '../../shared/api/year-end-queries.js';
import { dollarsToMinor, minorToDollars } from './books-format.js';

/**
 * Year-end: the accountant's deliverable, in the order an accountant works.
 *
 * First the checklist (what is still open in the year), then the slips,
 * then the Schedule 8, then the close, then the package. Each figure comes
 * from the same functions the rest of the product reads, so the package
 * says what the screens say.
 */

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function YearEndCard({
  organizationId,
  canEdit,
}: {
  organizationId: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear() - 1);
  const status = useQuery(yearEndStatusQuery(organizationId, fiscalYear));
  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: yearEndKeys.all(organizationId) });
    void queryClient.invalidateQueries({ queryKey: ['reports', organizationId] });
    void queryClient.invalidateQueries({ queryKey: ['ledger', organizationId] });
  };
  const s = status.data;

  return (
    <Card>
      <CardHeader
        title="Year-end"
        description={
          s === undefined
            ? 'Reading…'
            : `Fiscal ${String(s.fiscalYear)}: ${s.startOn} to ${s.endOn}.${s.closed === null ? (s.ended ? ' Not yet closed.' : ' Still running.') : ` Closed ${s.closed.closedAt.slice(0, 10)}.`}`
        }
      />
      <CardBody className="flex flex-col gap-6">
        {error === null ? null : <Alert tone="danger">{error}</Alert>}

        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-content-muted">Fiscal year</span>
            <Input
              inputMode="numeric"
              value={String(fiscalYear)}
              className="w-28"
              onChange={(event) => {
                const next = Number(event.target.value);
                if (Number.isInteger(next) && next >= 2000 && next <= 2100) setFiscalYear(next);
              }}
            />
          </label>
          {s === undefined ? null : (
            <FiscalYearEnd
              organizationId={organizationId}
              month={s.fiscalYearEndMonth}
              canEdit={canEdit}
              onChanged={refresh}
              onError={setError}
            />
          )}
        </div>

        {s === undefined ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <>
            <Checklist status={s} />
            <T5018 organizationId={organizationId} fiscalYear={fiscalYear} />
            <Schedule8
              organizationId={organizationId}
              fiscalYear={fiscalYear}
              canEdit={canEdit}
              posted={s.ccaEntryId !== null}
              ended={s.ended}
              onChanged={refresh}
              onError={setError}
            />
            <CloseTheYear
              organizationId={organizationId}
              fiscalYear={fiscalYear}
              status={s}
              canEdit={canEdit}
              onChanged={refresh}
              onError={setError}
            />
            <WorkingPapers
              organizationId={organizationId}
              fiscalYear={fiscalYear}
              canEdit={canEdit}
              onError={setError}
            />
          </>
        )}
      </CardBody>
    </Card>
  );
}

function Section({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2 border-t border-border pt-4">
      <h3 className="text-sm font-semibold text-content">{title}</h3>
      <p className="text-xs text-content-muted">{note}</p>
      {children}
    </section>
  );
}

function FiscalYearEnd({
  organizationId,
  month,
  canEdit,
  onChanged,
  onError,
}: {
  organizationId: string;
  month: number;
  canEdit: boolean;
  onChanged: () => void;
  onError: (m: string | null) => void;
}) {
  const change = useMutation({
    mutationFn: (next: number) => setFiscalYearEnd(organizationId, next),
    onSuccess: () => {
      onError(null);
      onChanged();
    },
    onError: (caught) =>
      onError(caught instanceof Error ? caught.message : 'The year-end was not changed.'),
  });
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-content-muted">Year ends in</span>
      <select
        value={month}
        disabled={!canEdit}
        onChange={(event) => change.mutate(Number(event.target.value))}
        className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
      >
        {MONTHS.map((name, index) => (
          <option key={name} value={index + 1}>
            {name}
          </option>
        ))}
      </select>
    </label>
  );
}

function Checklist({ status }: { status: { checklist: Record<string, number>; ended: boolean } }) {
  const c = status.checklist;
  const items: { label: string; count: number; to: string }[] = [
    {
      label: 'records still unsigned in the year',
      count: c['draftsInYear'] ?? 0,
      to: '/bookkeeping',
    },
    {
      label: 'bank lines still unexplained in the year',
      count: c['unmatchedBankLines'] ?? 0,
      to: '/bank',
    },
    {
      label: 'documents still waiting on a person',
      count: c['openReviewItems'] ?? 0,
      to: '/review',
    },
    { label: 'GST/HST periods not yet filed', count: c['unfiledHstPeriods'] ?? 0, to: '/close' },
    { label: 'months not yet closed', count: c['openMonths'] ?? 0, to: '/close' },
    {
      label: 'subcontractors missing slip details',
      count: c['t5018MissingDetails'] ?? 0,
      to: '/suppliers',
    },
  ];
  const open = items.filter((item) => item.count > 0);
  return (
    <Section
      title="Before the year closes"
      note={
        open.length === 0
          ? 'Nothing is open. The year is ready to close.'
          : `${String(open.length)} thing${open.length === 1 ? '' : 's'} still open. Each links to where it is dealt with.`
      }
    >
      {open.length === 0 ? null : (
        <ul className="flex flex-col gap-1 text-sm">
          {open.map((item) => (
            <li key={item.label}>
              <Link to={item.to} className="text-brand-700 hover:underline">
                {String(item.count)} {item.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function T5018({ organizationId, fiscalYear }: { organizationId: string; fiscalYear: number }) {
  const rows = useQuery(t5018Query(organizationId, fiscalYear));
  const data: T5018Row[] = rows.data ?? [];
  const reportable = data.filter((row) => row.reportable);
  const download = () => {
    const lines = [
      [
        'Recipient',
        'Legal name',
        'Business number',
        'Mailing address',
        'Amount paid (incl. GST/HST)',
        'Missing',
      ].join(','),
      ...reportable.map((row) =>
        [
          row.displayName,
          row.legalName ?? '',
          row.businessNumber ?? '',
          (row.mailingAddress ?? '').replaceAll('\n', ' '),
          (Number(row.paidMinor) / 100).toFixed(2),
          row.missing.join('; '),
        ]
          .map((cell) => `"${cell.replaceAll('"', '""')}"`)
          .join(','),
      ),
    ];
    const url = URL.createObjectURL(new Blob([lines.join('\r\n')], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `t5018-${String(fiscalYear)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Section
      title="T5018: payments to subcontractors"
      note={
        rows.data === undefined
          ? 'Reading…'
          : data.length === 0
            ? 'No supplier is marked as a subcontractor. Mark one on its supplier page and its payments appear here.'
            : `${String(reportable.length)} of ${String(data.length)} subcontractor${data.length === 1 ? '' : 's'} at or above $500 for the year, on a cash basis, GST/HST included.`
      }
    >
      {data.length === 0 ? null : (
        <>
          <table className="w-full text-sm tabular">
            <thead>
              <tr className="text-left text-xs text-content-muted">
                <th className="py-1 font-medium">Recipient</th>
                <th className="py-1 font-medium">Business number</th>
                <th className="py-1 text-right font-medium">Paid</th>
                <th className="py-1 font-medium">Slip</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.vendorId} className="border-t border-border">
                  <td className="py-1">
                    <Link
                      to="/suppliers/$vendorId"
                      params={{ vendorId: row.vendorId }}
                      className="text-brand-700 hover:underline"
                    >
                      {row.legalName ?? row.displayName}
                    </Link>
                  </td>
                  <td className="py-1 text-content-muted">{row.businessNumber ?? '—'}</td>
                  <td className="py-1 text-right">{formatMinor(row.paidMinor)}</td>
                  <td className="py-1 text-xs">
                    {!row.reportable ? (
                      <span className="text-content-muted">Under $500, no slip</span>
                    ) : row.missing.length === 0 ? (
                      <span className="text-success">Ready</span>
                    ) : (
                      <span className="text-danger">Missing {row.missing.join(' and ')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div>
            <Button
              size="sm"
              variant="secondary"
              onClick={download}
              disabled={reportable.length === 0}
            >
              Download the slip data
            </Button>
          </div>
        </>
      )}
    </Section>
  );
}

function Schedule8({
  organizationId,
  fiscalYear,
  canEdit,
  posted,
  ended,
  onChanged,
  onError,
}: {
  organizationId: string;
  fiscalYear: number;
  canEdit: boolean;
  posted: boolean;
  ended: boolean;
  onChanged: () => void;
  onError: (m: string | null) => void;
}) {
  const rows = useQuery(ccaQuery(organizationId, fiscalYear));
  const data: CcaRow[] = rows.data ?? [];
  const total = data.reduce((sum, row) => sum + BigInt(row.claimMinor), 0n);
  const save = useMutation({
    mutationFn: (input: {
      classId: string;
      openingMinor: string;
      dispositionsMinor: string;
      claimMinor: string | null;
    }) => setCcaInput(organizationId, { fiscalYear, ...input }),
    onSuccess: () => {
      onError(null);
      onChanged();
    },
    onError: (caught) =>
      onError(caught instanceof Error ? caught.message : 'The class was not saved.'),
  });
  const post = useMutation({
    mutationFn: () => postCca(organizationId, fiscalYear),
    onSuccess: () => {
      onError(null);
      onChanged();
    },
    onError: (caught) =>
      onError(caught instanceof Error ? caught.message : 'The CCA was not posted.'),
  });
  return (
    <Section
      title="Schedule 8: capital cost allowance"
      note={
        rows.data === undefined
          ? 'Reading…'
          : data.length === 0
            ? "No capital assets this year: no signed record has a capital line, and no class has an opening balance. Enter last year-end's UCC per class to start the schedule."
            : `Additions come from the year's signed capital lines, net of tax claimed back. ${fiscalYear >= 2024 && fiscalYear <= 2027 ? 'The accelerated investment incentive applies: the full rate on additions, no half-year rule.' : 'The half-year rule applies to additions.'} Claim less than the maximum in a loss year by typing the claim.`
      }
    >
      {data.length === 0 ? null : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[52rem] text-sm tabular">
            <thead>
              <tr className="text-left text-xs text-content-muted">
                <th className="py-1 font-medium">Class</th>
                <th className="w-24 py-1 text-right font-medium">Opening UCC</th>
                <th className="w-24 py-1 text-right font-medium">Additions</th>
                <th className="w-24 py-1 text-right font-medium">Dispositions</th>
                <th className="w-14 py-1 text-right font-medium">Rate</th>
                <th className="w-24 py-1 text-right font-medium">Maximum</th>
                <th className="w-24 py-1 text-right font-medium">Claim</th>
                <th className="w-24 py-1 text-right font-medium">Closing UCC</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <CcaLine
                  key={row.classId}
                  row={row}
                  editable={canEdit && !posted}
                  onSave={(input) => save.mutate({ classId: row.classId, ...input })}
                />
              ))}
              <tr className="border-t border-border font-semibold">
                <td colSpan={6} className="py-1">
                  Total CCA for the year
                </td>
                <td className="py-1 text-right">{formatMinor(total.toString())}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {!canEdit ? null : (
        <div className="flex flex-wrap items-center gap-2">
          {posted ? (
            <span className="text-xs text-success">
              Posted as an adjusting entry dated the last day of the year. Reverse it under
              Adjusting entries to change the inputs.
            </span>
          ) : (
            <Button
              size="sm"
              onClick={() => post.mutate()}
              isLoading={post.isPending}
              disabled={total === 0n || !ended}
            >
              Post the CCA entry
            </Button>
          )}
          <NewClassInput
            organizationId={organizationId}
            fiscalYear={fiscalYear}
            existing={data.map((row) => row.classId)}
            disabled={posted}
            onChanged={onChanged}
            onError={onError}
          />
        </div>
      )}
    </Section>
  );
}

function CcaLine({
  row,
  editable,
  onSave,
}: {
  row: CcaRow;
  editable: boolean;
  onSave: (input: {
    openingMinor: string;
    dispositionsMinor: string;
    claimMinor: string | null;
  }) => void;
}) {
  const [opening, setOpening] = useState(minorToDollars(row.openingMinor));
  const [dispositions, setDispositions] = useState(
    row.dispositionsMinor === '0' ? '' : minorToDollars(row.dispositionsMinor),
  );
  const [claim, setClaim] = useState(
    row.claimMinor === row.maxClaimMinor ? '' : minorToDollars(row.claimMinor),
  );
  const dirty =
    (dollarsToMinor(opening) ?? '0') !== row.openingMinor ||
    (dollarsToMinor(dispositions) ?? '0') !== row.dispositionsMinor ||
    (claim === '' ? row.maxClaimMinor : (dollarsToMinor(claim) ?? '0')) !== row.claimMinor;
  const money = (value: string, set: (v: string) => void, label: string) =>
    editable ? (
      <Input
        aria-label={label}
        inputMode="decimal"
        className="text-right"
        value={value}
        hasError={value !== '' && dollarsToMinor(value) === null}
        onChange={(event) => set(event.target.value)}
      />
    ) : (
      <span>{formatMinor(dollarsToMinor(value) ?? '0')}</span>
    );
  return (
    <tr className="border-t border-border align-top">
      <td className="py-1 pr-2">
        <span className="font-medium">Class {row.classCode}</span>
        <span className="block text-xs text-content-muted">{row.description}</span>
        {row.recaptureMinor !== '0' ? (
          <span className="block text-xs text-danger">
            Recapture {formatMinor(row.recaptureMinor)}: dispositions exceed the class.
          </span>
        ) : null}
      </td>
      <td className="py-1 pl-1">{money(opening, setOpening, 'Opening UCC')}</td>
      <td className="py-3 pl-1 text-right">{formatMinor(row.additionsMinor)}</td>
      <td className="py-1 pl-1">{money(dispositions, setDispositions, 'Dispositions')}</td>
      <td className="py-3 pl-1 text-right text-content-muted">
        {String(Math.round(Number(row.rate) * 1000) / 10)}%
      </td>
      <td className="py-3 pl-1 text-right">{formatMinor(row.maxClaimMinor)}</td>
      <td className="py-1 pl-1">
        {editable ? (
          <Input
            aria-label="Claim"
            inputMode="decimal"
            className="text-right"
            value={claim}
            placeholder={minorToDollars(row.maxClaimMinor)}
            hasError={claim !== '' && dollarsToMinor(claim) === null}
            onChange={(event) => setClaim(event.target.value)}
          />
        ) : (
          <span className="font-medium">{formatMinor(row.claimMinor)}</span>
        )}
        {editable && dirty ? (
          <button
            type="button"
            className="mt-1 block text-xs text-brand-700 hover:underline"
            onClick={() =>
              onSave({
                openingMinor: dollarsToMinor(opening) ?? '0',
                dispositionsMinor: dollarsToMinor(dispositions) ?? '0',
                claimMinor: claim === '' ? null : (dollarsToMinor(claim) ?? '0'),
              })
            }
          >
            Save
          </button>
        ) : null}
      </td>
      <td className="py-3 pl-1 text-right">{formatMinor(row.closingMinor)}</td>
    </tr>
  );
}

function NewClassInput({
  organizationId,
  fiscalYear,
  existing,
  disabled,
  onChanged,
  onError,
}: {
  organizationId: string;
  fiscalYear: number;
  existing: string[];
  disabled: boolean;
  onChanged: () => void;
  onError: (m: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [classId, setClassId] = useState('');
  const [opening, setOpening] = useState('');
  const reference = useQuery({ ...bookkeepingReferenceQuery(organizationId), enabled: open });
  const add = useMutation({
    mutationFn: () =>
      setCcaInput(organizationId, {
        fiscalYear,
        classId,
        openingMinor: dollarsToMinor(opening) ?? '0',
        dispositionsMinor: '0',
        claimMinor: null,
      }),
    onSuccess: () => {
      setOpen(false);
      setClassId('');
      setOpening('');
      onError(null);
      onChanged();
    },
    onError: (caught) =>
      onError(caught instanceof Error ? caught.message : 'The class was not added.'),
  });
  const options = (reference.data?.ccaClasses ?? []).filter((c) => !existing.includes(c.id));
  if (disabled) return null;
  if (!open) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        Add a class with an opening UCC
      </Button>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <select
        value={classId}
        onChange={(event) => setClassId(event.target.value)}
        className="h-9 rounded-[var(--radius-control)] border border-border-strong bg-surface px-2 text-sm"
      >
        <option value="">Class…</option>
        {options.map((c) => (
          <option key={c.id} value={c.id}>
            Class {c.classCode}: {c.description}
          </option>
        ))}
      </select>
      <Input
        aria-label="Opening UCC"
        inputMode="decimal"
        className="w-32 text-right"
        placeholder="Opening UCC"
        value={opening}
        onChange={(event) => setOpening(event.target.value)}
      />
      <Button
        size="sm"
        onClick={() => add.mutate()}
        isLoading={add.isPending}
        disabled={classId === '' || dollarsToMinor(opening) === null}
      >
        Add
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </span>
  );
}

function CloseTheYear({
  organizationId,
  fiscalYear,
  status,
  canEdit,
  onChanged,
  onError,
}: {
  organizationId: string;
  fiscalYear: number;
  status: {
    ended: boolean;
    closed: { closedAt: string } | null;
    lastReopen: { reopenedAt: string; reason: string | null } | null;
  };
  canEdit: boolean;
  onChanged: () => void;
  onError: (m: string | null) => void;
}) {
  const [reopening, setReopening] = useState(false);
  const [reason, setReason] = useState('');
  const close = useMutation({
    mutationFn: () => closeFiscalYear(organizationId, fiscalYear),
    onSuccess: () => {
      onError(null);
      onChanged();
    },
    onError: (caught) =>
      onError(caught instanceof Error ? caught.message : 'The year was not closed.'),
  });
  const reopen = useMutation({
    mutationFn: () => reopenFiscalYear(organizationId, fiscalYear, reason.trim()),
    onSuccess: () => {
      setReopening(false);
      setReason('');
      onError(null);
      onChanged();
    },
    onError: (caught) =>
      onError(caught instanceof Error ? caught.message : 'The year was not reopened.'),
  });
  return (
    <Section
      title="Close the year"
      note="One entry, dated the first day of the next year, carries the year's income and expense accounts to Retained earnings. Reports at year-end still show the year's earnings; reports after it show them retained. Reopened by reversal, with a reason."
    >
      {status.closed !== null ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Pill tone="success">Closed {status.closed.closedAt.slice(0, 10)}</Pill>
          {!canEdit ? null : reopening ? (
            <>
              <Input
                aria-label="Why reopen"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Why it is being reopened"
                className="min-w-64 flex-1"
              />
              <Button
                size="sm"
                variant="danger"
                onClick={() => reopen.mutate()}
                isLoading={reopen.isPending}
                disabled={reason.trim().length < 5}
              >
                Reopen
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setReopening(false)}>
                Keep it closed
              </Button>
            </>
          ) : (
            <button
              type="button"
              className="text-xs text-content-muted hover:text-danger"
              onClick={() => setReopening(true)}
            >
              Reopen
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {status.lastReopen === null ? null : (
            <span className="text-xs text-content-muted">
              Reopened {status.lastReopen.reopenedAt.slice(0, 10)}
              {status.lastReopen.reason === null ? '' : `: ${status.lastReopen.reason}`}.
            </span>
          )}
          {canEdit ? (
            <Button
              size="sm"
              onClick={() => close.mutate()}
              isLoading={close.isPending}
              disabled={!status.ended}
            >
              {status.ended ? `Close ${String(fiscalYear)}` : 'The year has not ended'}
            </Button>
          ) : null}
        </div>
      )}
    </Section>
  );
}

function WorkingPapers({
  organizationId,
  fiscalYear,
  canEdit,
  onError,
}: {
  organizationId: string;
  fiscalYear: number;
  canEdit: boolean;
  onError: (m: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const exports = useQuery(dataExportsQuery(organizationId));
  const request = useMutation({
    mutationFn: () => requestDataExport(organizationId, { kind: 'year_end', fiscalYear }),
    onSuccess: () => {
      onError(null);
      void queryClient.invalidateQueries({ queryKey: ['data-exports', organizationId] });
    },
    onError: (caught) =>
      onError(caught instanceof Error ? caught.message : 'The package was not started.'),
  });
  const download = async (item: DataExport, index: number) => {
    try {
      const { url } = await dataExportPartUrl(organizationId, item.id, index);
      window.location.assign(url);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : 'That part could not be downloaded.');
    }
  };
  const mine = (exports.data ?? []).filter(
    (item) =>
      item.kind === 'year_end' && item.fiscalYear === fiscalYear && item.status !== 'expired',
  );
  const inFlight = mine.some((item) => item.status === 'queued' || item.status === 'building');
  return (
    <Section
      title="Working papers"
      note="One zip: the statements at year-end, the ledger for the year, the GST/HST periods, the T5018 data, the Schedule 8, what was still open, and every document behind the year's signed records. Kept seven days."
    >
      {!canEdit ? null : (
        <div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => request.mutate()}
            isLoading={request.isPending}
            disabled={inFlight}
          >
            {inFlight ? 'Building the package…' : `Build the ${String(fiscalYear)} package`}
          </Button>
        </div>
      )}
      {mine.map((item) => (
        <div
          key={item.id}
          className="flex flex-col gap-1 rounded-[var(--radius-control)] border border-border px-3 py-2 text-sm"
        >
          <div className="flex items-center gap-2">
            <span>{new Date(item.createdAt).toLocaleString('en-CA')}</span>
            <Pill
              tone={
                item.status === 'ready'
                  ? 'success'
                  : item.status === 'failed'
                    ? 'danger'
                    : 'neutral'
              }
            >
              {item.status === 'ready' ? 'Ready' : item.status === 'failed' ? 'Failed' : 'Building'}
            </Pill>
          </div>
          {item.status === 'failed' && item.error !== null ? (
            <p className="text-xs text-danger">{item.error}</p>
          ) : null}
          {item.status === 'ready' ? (
            <ul className="flex flex-col gap-0.5">
              {item.parts.map((part, index) => (
                <li key={part.name}>
                  <button
                    type="button"
                    className="text-brand-600 underline hover:text-brand-700"
                    onClick={() => void download(item, index)}
                  >
                    {part.name}
                  </button>{' '}
                  <span className="text-xs text-content-muted">
                    {(part.bytes / 1_048_576).toFixed(1)} MB
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}
    </Section>
  );
}
